import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeApi } from "@/services/finance-api";
import type { InviteReceiptTextBody, InviteReceiptTextPatch } from "@/types/finance";

const INVITE_RECEIPT_TEXTS_PREFIX = ["finance", "invite-receipt-texts"] as const;

function inviteReceiptTextsKey(params: { agencia_id?: string; incluir_inativos?: boolean }) {
  return [...INVITE_RECEIPT_TEXTS_PREFIX, params] as const;
}

export function useInviteReceiptTexts(params: { agencia_id?: string; incluir_inativos?: boolean }) {
  return useQuery({
    queryKey: inviteReceiptTextsKey(params),
    queryFn: () => financeApi.listInviteReceiptTexts(params),
  });
}

/** A text change can turn the receipt confirmation on or off in open proposals. */
function invalidateAfterChange(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: INVITE_RECEIPT_TEXTS_PREFIX });
  queryClient.invalidateQueries({ queryKey: ["finance", "proposta"] });
}

export function useCreateInviteReceiptText() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteReceiptTextBody) => financeApi.createInviteReceiptText(body),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

export function useUpdateInviteReceiptText() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ textId, body }: { textId: string; body: InviteReceiptTextPatch }) =>
      financeApi.updateInviteReceiptText(textId, body),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}
